// src/pages/ProfilePage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import authService from '../services/authService';
import subscriptionService from '../services/subscriptionService'; // <-- 1. IMPORTAR O SERVIÇO DE ASSINATURA
import useAuth from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import './ProfilePage.css';
import { FaCamera, FaCreditCard, FaLock } from 'react-icons/fa';

// --- NOVA FUNÇÃO HELPER PARA FORMATAR A DATA ---
const formatDate = (dateString) => {
  if (!dateString) return 'Não disponível';
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    return 'Data inválida';
  }
};


const ProfilePage = () => {
  const { user, updateUserAndNotify } = useAuth();

  const [formData, setFormData] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // --- 2. NOVOS ESTADOS PARA DADOS DA ASSINATURA ---
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const formatPhone = (value) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');
    if (value.length > 10) {
      return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else {
      return value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setAvatarPreview(user.avatar);

      // --- 3. LÓGICA PARA BUSCAR A ASSINATURA ---
      const fetchSubscriptionDetails = async () => {
        setLoadingSubscription(true);
        try {
          const response = await subscriptionService.getUserSubscription();
          if (response.success && response.data) {
            setSubscription(response.data);
          }
        } catch (error) {
          console.error("Falha ao buscar detalhes da assinatura:", error);
          // Opcional: setar uma mensagem de erro específica para a assinatura
        } finally {
          setLoadingSubscription(false);
        }
      };

      fetchSubscriptionDetails();
    }
  }, [user]);

  const isPasswordValid = useMemo(() => {
    return !formData?.newPassword || formData.newPassword === formData.confirmPassword;
  }, [formData]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!window.confirm("Tem certeza que deseja cancelar sua assinatura? Você perderá o acesso aos benefícios no final do ciclo de faturamento atual.")) {
      return;
    }
    setLoading(true);
    try {
        const response = await subscriptionService.cancelSubscription();
        if (response.success) {
            setMessage("Assinatura cancelada com sucesso. As cobranças futuras foram interrompidas.");
            setStatus('success');
            // Atualiza os dados da assinatura na tela
            const updatedSub = await subscriptionService.getUserSubscription();
            if (updatedSub.success) setSubscription(updatedSub.data);
        }
    } catch (error) {
        setMessage(error.message || "Erro ao cancelar a assinatura.");
        setStatus('error');
    } finally {
        setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) { setMessage('As senhas não coincidem'); setStatus('error'); return; }
    setLoading(true);
    setMessage('');
    try {
      let successMessages = [];
      let latestUserData = { ...user };

      if (avatarFile) {
        const avatarRes = await authService.updateAvatar(avatarFile);
        latestUserData = { ...latestUserData, avatar: avatarRes.data.avatar };
        setAvatarFile(null);
        successMessages.push('Foto de perfil atualizada');
      }

      const phoneChanged = formData.phone.replace(/\D/g, '') !== (user.phone || '');
      if (formData.name !== user.name || formData.email !== user.email || phoneChanged) {
        const userRes = await authService.updateUser({ name: formData.name, email: formData.email, phone: formData.phone.replace(/\D/g, '') });
        latestUserData = { ...latestUserData, ...userRes.data };
        successMessages.push('Dados do perfil atualizados');
      }

      if (formData.newPassword) {
        await authService.updatePassword({ currentPassword: formData.currentPassword, newPassword: formData.newPassword });
        successMessages.push('Senha atualizada');
        setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      }

      if (successMessages.length > 0) {
        updateUserAndNotify(latestUserData);
        setMessage(successMessages.join(' e ') + ' com sucesso!');
        setStatus('success');
      } else {
        setMessage('Nenhuma alteração foi feita.');
        setStatus('success');
      }
    } catch (error) {
      setMessage(error.message || 'Erro ao atualizar perfil');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const messageBox = useMemo(() => {
    if (!message) return null;
    return <div className={`message-box ${status}`}>{message}</div>;
  }, [message, status]);

  if (!formData || !avatarPreview) {
    return <div className="profile-container"><p style={{ color: 'white' }}>Carregando perfil...</p></div>;
  }
  
  // --- 4. LÓGICA ATUALIZADA PARA USAR O NOVO ESTADO `subscription` ---
  const isSubscribed = subscription && subscription.status === 'active';
  const planName = subscription?.plan?.name || 'Gratuito';

  return (
    <div className="profile-container">
      <div className="profile-card">
        <h2>Meu Perfil</h2>
        {messageBox}
        <form onSubmit={handleSubmit} className="profile-form">
          {/* ... (seção do avatar e formulário de dados pessoais sem alterações) ... */}
           <div className="avatar-section">
            <label htmlFor="avatar-upload" className="profile-avatar-wrapper">
              <img src={avatarPreview} alt="Avatar" className="profile-avatar" />
              <div className="avatar-overlay"><FaCamera size={24} /></div>
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="form-group">
            <label>Nome:</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>E-mail:</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Telefone:</label>
            <input type="tel" name="phone" value={formatPhone(formData.phone)} onChange={handleChange} maxLength="15" placeholder="(XX) XXXXX-XXXX" />
          </div>

          <hr className="divider" />
          
          {/* --- 5. BLOCO DE ASSINATURA TOTALMENTE ATUALIZADO --- */}
          <div className="subscription-section">
            <h3><FaCreditCard /> Minha Assinatura</h3>
            {loadingSubscription ? (
              <p>Carregando dados da assinatura...</p>
            ) : (
              <div className="subscription-details">
                <div className="detail-item">
                  <span className="detail-label">Plano Atual:</span>
                  <span className="detail-value">{planName}</span>
                </div>
                
                {isSubscribed && subscription.nextBillingDate && (
                  <div className="detail-item">
                    <span className="detail-label">Próxima Cobrança:</span>
                    <span className="detail-value">{formatDate(subscription.nextBillingDate)}</span>
                  </div>
                )}

                <div className="detail-item">
                  <span className="detail-label">Status:</span>
                  <span className={`subscription-status ${subscription?.status || 'free'}`}>
                    {subscription?.status === 'active' ? 'Ativo' :
                     subscription?.status === 'pending' ? 'Pendente' :
                     subscription?.status === 'cancelled' ? 'Cancelado' : 'Gratuito'}
                  </span>
                </div>
                
                {isSubscribed ? (
                    <button type="button" onClick={handleCancelSubscription} className="cancel-subscription-btn" disabled={loading}>
                        {loading ? 'Cancelando...' : 'Cancelar Assinatura'}
                    </button>
                ) : (
                    subscription?.status !== 'pending' && (
                        <p className="upgrade-message">
                            Aprimore sua experiência! <Link to="/pricing" className="upgrade-link">Veja nossos planos</Link>.
                        </p>
                    )
                )}
              </div>
            )}
          </div>
          
          <hr className="divider" />
          
          {/* ... (formulário de senha sem alterações) ... */}
           <div className="form-group">
            <label><FaLock /> Alterar Senha</label>
            <input type="password" name="currentPassword" value={formData.currentPassword} onChange={handleChange} placeholder="Senha Atual (obrigatória para alterar)" />
          </div>

          <div className="form-group">
            <input type="password" name="newPassword" value={formData.newPassword} onChange={handleChange} placeholder="Nova Senha" />
          </div>

          <div className="form-group">
            <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="Confirmar Nova Senha" />
          </div>

          <button type="submit" className="save-btn" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;